import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ApiChopin,
  API_CHOPIN,
  IS_LOGGED_IN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_KEY,
} from '../config/index';
import { StorageService } from './storage.service';
import { from, Observable } from 'rxjs';
import { mapTo, switchMap } from 'rxjs/operators';
import { parseJwt } from '@shared/utils';

export interface LoginData {
  email: string;
  password: string;
}

export interface SessionToken {
  token: string;
  refresh_token: string;
}

export interface JWTToken {
  email: string;
  exp: number;
  iat: number;
  id: number;
  roles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  constructor(
    @Inject(API_CHOPIN) private api: ApiChopin,
    private storage: StorageService,
    private http: HttpClient
  ) {}

  login(data: LoginData): Observable<SessionToken> {
    return this.http.post<SessionToken>(this.api.LOGIN(), data).pipe(
      switchMap((session) => {
        return from(this.setAuthToken(session)).pipe(mapTo(session));
      })
    );
  }

  refreshToken(): Observable<SessionToken> {
    return from(this.getRefreshToken()).pipe(
      switchMap((refresh_token) => {
        return this.http
          .post<SessionToken>(this.api.REFRESH_TOKEN(), { refresh_token })
          .pipe(
            switchMap((session) => {
              return from(this.setAuthToken(session)).pipe(mapTo(session));
            })
          );
      })
    );
  }

  loginWithFacebook(code: string): Observable<SessionToken> {
    return this.http
      .get<SessionToken>(this.api.SOCIAL_LOGIN_FB(), { params: { code } })
      .pipe(
        switchMap((session) => {
          return from(this.setAuthToken(session)).pipe(mapTo(session));
        })
      );
  }

  loginWithGoogle(code: string): Observable<SessionToken> {
    return this.http
      .get<SessionToken>(this.api.SOCIAL_LOGIN_GOOGLE(), { params: { code } })
      .pipe(
        switchMap((session) => {
          return from(this.setAuthToken(session)).pipe(mapTo(session));
        })
      );
  }

  loginWithApple(code: string, token: string): Observable<SessionToken> {
    return this.http
      .get<SessionToken>(this.api.SOCIAL_LOGIN_APPLE(), {
        params: { code, token },
      })
      .pipe(
        switchMap((session) => {
          return from(this.setAuthToken(session)).pipe(mapTo(session));
        })
      );
  }

  async isLoggedIn(): Promise<boolean> {
    const logged = await this.storage.getItem(IS_LOGGED_IN_KEY);
    return !!logged;
  }

  async getAuthToken(): Promise<string> {
    return await this.storage.getItem(TOKEN_KEY);
  }

  async getRefreshToken(): Promise<string> {
    return await this.storage.getItem(REFRESH_TOKEN_KEY);
  }

  async getTokenData(): Promise<JWTToken> {
    const token = await this.getAuthToken();
    return token && parseJwt(token);
  }

  async setAuthToken(data: SessionToken) {
    await this.storage.setItem(TOKEN_KEY, data.token);
    await this.storage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    await this.storage.setItem(IS_LOGGED_IN_KEY, 1);
  }

  async logout(): Promise<void> {
    await this.storage.removeItem(IS_LOGGED_IN_KEY);
    await this.storage.removeItem(REFRESH_TOKEN_KEY);
    await this.storage.removeItem(TOKEN_KEY);
  }

  passwordResetInit(email: string): Observable<boolean> {
    return this.http
      .post<void>(this.api.PASSWORD_RESET(), { email })
      .pipe(mapTo(true));
  }

  passwordChange(plainPassword: string, token: string): Observable<boolean> {
    return this.http
      .post<void>(this.api.PASSWORD_RESET_CHANGE(), { plainPassword, token })
      .pipe(mapTo(true));
  }
}
